import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BZAPIService from '../../services/BZAPIService';

// Global ServiceNow setup indicator, shown in the top bar on every page.
// Configured-only signal (no live connection test): green when the tenant has a
// ServiceNow integration configured, red when it doesn't, amber while the
// initial check is in flight. Click → Platform Integrations.
//
// Statuses: loading → configured | not-configured | hidden. 'hidden' is set when
// the setting answers 403 (it is admin-only): a non-admin can't set ServiceNow
// up, so the chip renders nothing and has no DOT or LABEL entry (#1265).
const DOT = {
  loading: 'var(--warn)',
  configured: 'var(--ok)',
  'not-configured': 'var(--bad)'
};
const LABEL = {
  loading: 'ServiceNow: checking…',
  configured: 'ServiceNow: connected',
  'not-configured': 'ServiceNow: not configured — click to set up'
};

const SnStatusChip = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        // allowForbidden: a non-admin's 403 hides the chip instead of sending
        // every page to /authentication/forbidden (#1265).
        const sn = await BZAPIService.getSetting('servicenow', { allowForbidden: true });
        if (cancelled) return;
        const configured = !!(sn && typeof sn === 'object' && Object.keys(sn).length);
        setStatus(configured ? 'configured' : 'not-configured');
      } catch (e) {
        if (cancelled) return;
        // A 403 hides the chip; an unreachable/absent setting reads as not configured.
        setStatus(e.status === 403 ? 'hidden' : 'not-configured');
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === 'hidden') return null;

  return (
    <button
      type="button"
      title={LABEL[status]}
      aria-label={LABEL[status]}
      onClick={() => navigate('/platform/integrations')}
      className="bz-btn bz-btn--ghost bz-btn--sm"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 6px', border: 'none', background: 'transparent' }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '.3px', color: 'var(--bz-blue-1)' }}>SN</span>
      <span style={{ width: 8, height: 8, borderRadius: 999, background: DOT[status], flexShrink: 0 }} />
    </button>
  );
};

export default SnStatusChip;
